import { DURATION, EASE } from "@/lib/motion"
import { subscribeCartBump } from "@/lib/fly-to-cart"
import { useAnimationControls, useReducedMotion } from "motion/react"
import { useEffect } from "react"

type BumpScaleOptions = {
  /** Peak scale on an addition — swells outwards. */
  add?: number
  /** Peak scale on a removal — pulls inwards, the same move mirrored. */
  remove?: number
  /** Spring that carries it back to rest. Under-damped on purpose: the
   *  overshoot is what reads as "elastic" instead of a plain scale tween. */
  stiffness?: number
  damping?: number
  mass?: number
}

/**
 * Elastic squash-and-settle, played whenever the bag gains or loses an item.
 *
 * Driven by the cart-bump event (the ghost landing, or a `−` tap), never by the
 * total changing: reacting to the total would also fire on every unrelated
 * update — hydration, a reorder, a price refresh from the catalog poll.
 */
export function useCartBumpScale({
  add = 1.06,
  remove = 0.95,
  stiffness = 480,
  damping = 9,
  mass = 0.7,
}: BumpScaleOptions = {}) {
  const controls = useAnimationControls()
  const reduceMotion = useReducedMotion()

  useEffect(() => {
    if (reduceMotion) return

    return subscribeCartBump((kind) => {
      void controls
        .start({
          scale: kind === "add" ? add : remove,
          transition: { duration: DURATION.instant, ease: EASE.enter },
        })
        .then(() =>
          controls.start({
            scale: 1,
            transition: { type: "spring", stiffness, damping, mass },
          }),
        )
    })
  }, [controls, reduceMotion, add, remove, stiffness, damping, mass])

  return controls
}

/** Companion wiggle for an icon riding the same bump. */
export function useCartBumpRotate(degrees = 14) {
  const controls = useAnimationControls()
  const reduceMotion = useReducedMotion()

  useEffect(() => {
    if (reduceMotion) return

    return subscribeCartBump((kind) => {
      const sign = kind === "add" ? -1 : 1
      void controls.start({
        rotate: [0, degrees * sign, -degrees * 0.7 * sign, degrees * 0.35 * sign, 0],
        transition: { duration: DURATION.emphasis, ease: EASE.enter },
      })
    })
  }, [controls, reduceMotion, degrees])

  return controls
}
